<template>
  <div>
    <div v-if="!current_event || !current_event.event_id">
      Valitse event
    </div>
    <div v-else>
      <div class="mb-2">
        <div class="font-weight-bold">
          Palon tekijä
        </div>
        <div class="text-caption grey--text">
          1-9 lisää palon pelaajalle, Shift + 1-9 poistaa. C tyhjentää.
        </div>
        <div class="text-caption mt-1" :class="markedOutsTotal > 0 ? 'success--text' : 'grey--text'">
          Merkitty {{ markedOutsTotal }} / {{ outsToMark }} paloa
        </div>
      </div>
      <div class="d-flex flex-column">
        <v-btn
          v-for="(player, index) in shownOutfielders"
          :key="player.id"
          small
          :outlined="madeOutCount(player.id) === 0"
          :color="madeOutCount(player.id) > 0 ? 'primary' : undefined"
          class="mb-1 justify-start"
          @click="addMadeOut(player.id)"
        >
          <span class="mr-2 grey--text">{{ playerShortcutLabel(index) }}</span>
          <span class="flex-grow-1 text-left">{{ playerLabel(player) }}</span>
          <v-chip
            v-if="madeOutCount(player.id) > 0"
            x-small
            label
            class="ml-2"
          >
            {{ madeOutCount(player.id) }}
          </v-chip>
        </v-btn>
        <v-btn
          v-if="extraOutfielders.length"
          small
          text
          color="primary"
          class="mt-1"
          @click="show_extra_outfielders = !show_extra_outfielders"
        >
          {{ show_extra_outfielders ? 'Piilota lisäpelaajat' : 'Näytä pelaajat 10-12' }}
        </v-btn>
      </div>
      <div class="font-weight-bold">
        Pesämuutoksissa X
      </div>
      <div class="mt-2 text-caption grey--text">
        Tässä vaiheessa näytetään vain eventit, joissa jokin pesämuutos on X.
      </div>
      <div class="mt-3">
        <v-chip
          v-for="(value, base) in current_event.base_changes"
          :key="base"
          class="mr-1 mb-1"
          small
          label
          :color="value === 'X' ? 'primary' : undefined"
          :outlined="value !== 'X'"
        >
          {{ base }}: {{ value || '-' }}
        </v-chip>
      </div>
      <EventArrows @save="save" />
      <v-divider class="my-4" />
      <div class="text-caption grey--text">
        Debug
      </div>
      <v-card flat outlined class="pa-2 debug-block">
        <div><strong>Event:</strong> {{ current_event.event_id }}</div>
        <div class="mt-1">
          <strong>Base changes:</strong>
          <v-chip
            v-for="(value, base) in current_event.base_changes"
            :key="'debug-' + base"
            x-small
            label
            class="ml-1"
            :color="value === 'X' ? 'primary' : undefined"
            :outlined="value !== 'X'"
          >
            {{ base }}: {{ value || '-' }}
          </v-chip>
        </div>
        <div class="mt-2">
          <strong>Already saved:</strong>
          <span v-if="!eventMadeOuts(current_event).length">none</span>
          <div v-for="madeOut in eventMadeOuts(current_event)" :key="'saved-' + madeOut.player_id">
            {{ madeOut.player_name }}: {{ madeOut.outs }}
          </div>
        </div>
        <div class="mt-2">
          <strong>Selected now:</strong>
          <span v-if="!madeOutsPayload().length">none</span>
          <div v-for="madeOut in madeOutsPayload()" :key="'local-' + madeOut.player_id">
            {{ madeOut.player_name }}: {{ madeOut.outs }}
          </div>
        </div>
        <div class="mt-2">
          <strong>Will save:</strong>
          <span v-if="!madeOutsPayload().length">empty made_outs array</span>
          <div v-for="madeOut in madeOutsPayload()" :key="'next-' + madeOut.player_id">
            player_id {{ madeOut.player_id }},
            external {{ madeOut.player_external_id || '-' }},
            {{ madeOut.player_name }},
            outs {{ madeOut.outs }}
          </div>
        </div>
      </v-card>
      <div class="d-flex align-center mt-3">
        <div class="text-caption grey--text flex-grow-1">
          Raw current event
        </div>
        <v-btn x-small text color="primary" @click="copyRawCurrentEvent">
          {{ raw_event_copied ? 'Kopioitu' : 'Kopioi' }}
        </v-btn>
      </div>
      <pre class="raw-event-block">{{ rawCurrentEvent }}</pre>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import EventArrows from '@/components/analysis/analysis_components/EventArrows.vue';

export default {
  name: 'MadeOutsAnalysis',
  components: {
    EventArrows
  },
  data: () => ({
    made_outs_by_player_id: {},
    show_extra_outfielders: false,
    raw_event_copied: false
  }),
  shortcuts: {
    keydown: function(e) {
      const keyCode = e.keyCode || e.which
      const isNumber = keyCode >= 49 && keyCode <= 57

      if(isNumber) {
        const index = keyCode - 49
        if(e.shiftKey) this.removeMadeOutByIndex(index)
        else this.addMadeOutByIndex(index)
        e.preventDefault()
        return
      }

      if(keyCode === 67) {
        this.clearMadeOuts()
        e.preventDefault()
      }
    },
  },
  created() {
    this.SET_SHOW_MEASURE_TAPE(false)
    this.SET_EVENTS_FILTER_FUNCTION(this.hasOutInBaseChanges)
    this.setMadeOutsFromEvent()
  },
  beforeDestroy() {
    this.SET_EVENTS_FILTER_FUNCTION(null)
  },
  computed: {
    ...mapGetters('analysis', [
      'current_event'
    ]),
    ...mapGetters('game', [
      'game_players'
    ]),
    fieldingPlayers() {
      const side = this.current_event.hitting === 'home' ? 'away' : 'home'
      return (this.game_players?.[side] || []).slice(0, 12)
    },
    outfielders() {
      return this.fieldingPlayers.slice(0, 9)
    },
    extraOutfielders() {
      return this.fieldingPlayers.slice(9, 12)
    },
    shownOutfielders() {
      if(this.show_extra_outfielders) return this.outfielders.concat(this.extraOutfielders)
      return this.outfielders
    },
    outsToMark() {
      return 3
    },
    markedOutsTotal() {
      return Object.values(this.made_outs_by_player_id).reduce((total, count) => total + count, 0)
    },
    remainingOuts() {
      return this.outsToMark - this.markedOutsTotal
    },
    rawCurrentEvent() {
      return JSON.stringify(this.current_event, null, 2)
    }
  },
  methods: {
    hasOutInBaseChanges(event) {
      return Object.values(event?.base_changes || {}).some(value => value === 'X')
    },
    eventMadeOuts(event) {
      if(Array.isArray(event?.made_outs)) return event.made_outs

      return []
    },
    setMadeOutsFromEvent() {
      this.made_outs_by_player_id = this.eventMadeOuts(this.current_event).reduce((acc, madeOut) => {
        if(madeOut.player_id) acc[madeOut.player_id] = madeOut.outs
        return acc
      }, {})
    },
    madeOutsPayload() {
      return this.fieldingPlayers
        .map(player => {
          const outs = this.madeOutCount(player.id)
          if(!outs) return null

          return {
            player_id: player.id,
            player_external_id: player.external_service_id || player.player_external_id || player.external_id,
            player_name: player.player_name,
            outs
          }
        })
        .filter(Boolean)
    },
    save() {
      const new_event = {
        ...this.current_event,
        made_outs: this.madeOutsPayload(),
        phase_done: Math.max(this.current_event.phase_done, 2)
      }

      this.$emit('eventSaved', new_event)
    },
    addMadeOut(player_id) {
      if(this.markedOutsTotal >= this.outsToMark) return

      const next = {
        ...this.made_outs_by_player_id,
        [player_id]: this.madeOutCount(player_id) + 1
      }

      this.made_outs_by_player_id = next
    },
    removeMadeOut(player_id) {
      const count = this.madeOutCount(player_id)
      if(count <= 0) return

      const next = { ...this.made_outs_by_player_id }
      if(count === 1) delete next[player_id]
      else next[player_id] = count - 1

      this.made_outs_by_player_id = next
    },
    addMadeOutByIndex(index) {
      const player = this.outfielders[index]
      if(!player) return

      this.addMadeOut(player.id)
    },
    removeMadeOutByIndex(index) {
      const player = this.outfielders[index]
      if(!player) return

      this.removeMadeOut(player.id)
    },
    madeOutCount(player_id) {
      return this.made_outs_by_player_id[player_id] || 0
    },
    playerLabel(player) {
      return `${player.pos || player.num || '-'} ${player.player_name}`
    },
    playerShortcutLabel(index) {
      if(index < 9) return index + 1
      return ''
    },
    clearMadeOuts() {
      this.made_outs_by_player_id = {}
    },
    copyRawCurrentEvent() {
      navigator.clipboard.writeText(this.rawCurrentEvent)
        .then(() => {
          this.raw_event_copied = true
          setTimeout(() => {
            this.raw_event_copied = false
          }, 1500)
        })
    },
    ...mapMutations('analysis', [
      'SET_EVENTS_FILTER_FUNCTION',
      'SET_SHOW_MEASURE_TAPE'
    ])
  },
  watch: {
    'current_event.event_id'() {
      this.show_extra_outfielders = false
      this.setMadeOutsFromEvent()
    }
  },
}
</script>

<style scoped>
.debug-block {
  background: #f5f5f5;
  color: #555;
  font-size: 11px;
  line-height: 1.4;
  max-height: 260px;
  overflow: auto;
}

.raw-event-block {
  background: #f5f5f5;
  border-radius: 4px;
  color: #555;
  font-size: 11px;
  line-height: 1.4;
  max-height: 320px;
  overflow: auto;
  padding: 8px;
  white-space: pre-wrap;
}
</style>
